import { useCallback, useEffect, useRef, useState } from 'react'
import { palettePrimaryDark, palettePrimaryMain as ACCENT, palettePrimaryMutedSurface } from '@/theme/designTokens'

import type { ReactNode } from 'react'
import {
  Navigate,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import CheckIcon from '@mui/icons-material/Check'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { ErrorState } from '@/shared/components/ErrorState'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useToast } from '@/shared/toast/ToastProvider'
import { useSessionStore } from '@/stores/useSessionStore'
import { useCampaign } from '../api/campaign-queries'
import { useLaunchCampaign } from '../api/campaign-mutations'
import {
  useTeamMembersForAssignment,
  type TeamMemberRow,
} from '../api/team-queries'
import { CampaignWizardChrome } from '../components/CampaignWizardChrome'
import { formatCampaignWeekTimelineValue } from '../lib/campaign-week-timeline'
import { LAUNCH_SUMMARY_PANEL_GRADIENT } from '../lib/launch-panel-gradient'
import {
  messageFromCampaignLaunchError,
  messageFromUnknownApiError,
} from '../lib/mapApiUnknownError'
import {
  canAccessTeamLaunchWizard,
  isCampaignLaunched,
} from '../lib/campaignResumeWizardPath'
import {
  isServerTeamWizardReady,
  readWizardContinueToTeamState,
  shouldHoldWizardContinueOnTeam,
  shouldInterceptTeamWizardBrowserBack,
  WIZARD_CONTINUE_TO_TEAM_STATE,
} from '../lib/campaignWizardNavigation'
import type { Campaign } from '../types'

const EMPTY_TEAM_ROWS: TeamMemberRow[] = []

const TEXT_PRIMARY = '#0f1729'
const TEXT_SECONDARY = '#65758b'
const BORDER_DEFAULT = '#e2e8f0'
const ROW_SELECTED_BG = '#d8ecf8'
const ROW_SELECTED_BORDER = '#e7e9f1'

const AVATAR_BACKGROUNDS = ['#e7e9f1', '#1976d2', '#64e2fd', '#10b981', '#3b82f6']
const AVATAR_FG = ['#0c395e', '#fff', '#0c395e', '#fff', '#fff']

/** Distinct ids that may identify the same person (JWT sub vs tenant row id). */
function memberIdentityKeys(member: TeamMemberRow): string[] {
  const keys = new Set<string>()
  const pu = member.platformUserId?.trim()
  if (pu) keys.add(pu)
  const rowId = member._id?.trim()
  if (rowId) keys.add(rowId)
  return [...keys]
}

function memberInitials(firstName: string, lastName: string): string {
  const a = firstName.trim().charAt(0)
  const b = lastName.trim().charAt(0)
  return `${a}${b}`.toUpperCase() || '?'
}

function tenantTeamRoleLabel(role: string): string {
  const r = role.toUpperCase()
  if (r === 'ADMIN') return 'Admin'
  if (r === 'EDITOR') return 'Editor'
  if (r === 'VIEWER') return 'Viewer'
  return role
}

/** Pill styling for tenant team role (not the campaign-creator "Owner" badge). */
function tenantTeamRolePillStyle(role: string): { bgcolor: string; color: string } {
  const r = role.toUpperCase()
  if (r === 'ADMIN')
    return { bgcolor: '#bdddcb', color: '#0f766a' }
  if (r === 'EDITOR')
    return { bgcolor: '#fae8da', color: '#f59e0b' }
  if (r === 'VIEWER')
    return { bgcolor: palettePrimaryMutedSurface, color: ACCENT }
  return { bgcolor: '#e2e8f0', color: '#65758b' }
}

const CAMPAIGN_CREATOR_PILL = {
  bgcolor: '#bdddcb',
  color: '#0f766a',
} as const

function isCampaignCreator(
  member: TeamMemberRow,
  campaign: Campaign,
  options: {
    sessionUserId: string | undefined
    assignableMemberCount: number
  },
): boolean {
  const cid = campaign.createdByPlatformUserId?.trim()
  const keys = memberIdentityKeys(member)
  if (cid) {
    return keys.includes(cid)
  }
  const sid = options.sessionUserId?.trim()
  if (
    sid &&
    options.assignableMemberCount === 1 &&
    keys.includes(sid)
  ) {
    return true
  }
  return false
}

function memberAssignmentPill(
  member: TeamMemberRow,
  campaign: Campaign,
  options: {
    sessionUserId: string | undefined
    assignableMemberCount: number
  },
): { label: string; bgcolor: string; color: string } {
  if (isCampaignCreator(member, campaign, options)) {
    return { label: 'Owner', ...CAMPAIGN_CREATOR_PILL }
  }
  return {
    label: tenantTeamRoleLabel(member.role),
    ...tenantTeamRolePillStyle(member.role),
  }
}

function formatBudgetDisplay(budget: number | null): string {
  if (budget == null || !Number.isFinite(budget)) return '—'
  return budget.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

function weekTimelineWeeks(campaign: Campaign): string {
  return formatCampaignWeekTimelineValue(campaign.startDate, campaign.endDate)
}

function paddedLoiCount(n: number): string {
  return n < 100 ? String(n).padStart(2, '0') : String(n)
}

const STAT_CARD_BG = '#1F3455'
const STAT_ICON_WELL_BG = '#1C3D62'
const STAT_ICON_COLOR = '#B0E5FF'
const STAT_CARD_WIDTH_PX = 254
const STAT_CARD_HEIGHT_PX = 54

function ReadyLaunchStatCard({
  icon,
  value,
  label,
}: Readonly<{
  icon: ReactNode
  value: string
  label: string
}>) {
  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        width: { xs: '100%', sm: `${STAT_CARD_WIDTH_PX}px` },
        maxWidth: '100%',
        height: `${STAT_CARD_HEIGHT_PX}px`,
        flex: { xs: '1 1 auto', sm: '1 1 0' },
        minWidth: { sm: 0 },
        flexShrink: { sm: 1 },
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 0,
        borderRadius: '8px',
        bgcolor: STAT_CARD_BG,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '8px',
          bgcolor: STAT_ICON_WELL_BG,
          '& .MuiSvgIcon-root': {
            fontSize: 22,
            width: 22,
            height: 22,
            color: STAT_ICON_COLOR,
            display: 'block',
          },
        }}
        aria-hidden
      >
        {icon}
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          textAlign: 'left',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Box
          component="div"
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 700,
            fontSize: 16,
            lineHeight: '22px',
            letterSpacing: '-0.15px',
            textAlign: 'left',
            color: `var(--b-primary-1, ${ACCENT})`,
          }}
        >
          {value}
        </Box>
        <Box
          component="div"
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 300,
            fontSize: 12,
            lineHeight: '14px',
            textAlign: 'left',
            color: '#FFF',
            mt: 0.25,
          }}
        >
          {label}
        </Box>
      </Box>
    </Box>
  )
}

/** Unchecked — Figma: 18px box, 2px border palettePrimaryMain, rounded 2px (node deselect reference). */
function TeamCheckboxUncheckedIcon() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        width: 18,
        height: 18,
        borderRadius: '2px',
        border: `2px solid ${ACCENT}`,
        bgcolor: 'transparent',
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    />
  )
}

/** Checked — Figma: filled #7a97ad with white check (first row selection reference). */
function TeamCheckboxCheckedIcon() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        borderRadius: '2px',
        bgcolor: '#7a97ad',
        flexShrink: 0,
      }}
    >
      <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
    </Box>
  )
}

function teamLaunchWizardRedirect(
  holdWizardContinue: boolean,
  campaignView: Campaign,
  campaignId: string,
): ReactNode | null {
  if (holdWizardContinue) return null
  if (isCampaignLaunched(campaignView)) {
    return <Navigate to={`/campaigns/${campaignId}`} replace />
  }
  if (!canAccessTeamLaunchWizard(campaignView)) {
    if (campaignView.status === 'goal_analysis') {
      return (
        <Navigate
          to={`/campaigns/${campaignId}/goal-analysis`}
          replace
        />
      )
    }
    return <Navigate to={`/campaigns/${campaignId}/setup`} replace />
  }
  return null
}

type TeamLaunchPageShellInput = Readonly<{
  role: string | undefined
  isLoading: boolean
  isError: boolean
  pinnedContinueCampaign: Campaign | undefined
  campaignView: Campaign | undefined
  holdWizardContinue: boolean
  campaignId: string
  refetch: () => void | Promise<unknown>
}>

function resolveTeamLaunchPageShell(input: TeamLaunchPageShellInput): ReactNode | null {
  if (input.role !== 'ADMIN') {
    return <Navigate to="/unauthorized" replace />
  }
  if ((input.isLoading && !input.pinnedContinueCampaign) || !input.campaignView) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }
  if (input.isError && !input.pinnedContinueCampaign) {
    return (
      <ErrorState
        onRetry={() => {
          Promise.resolve(input.refetch()).catch(() => undefined)
        }}
      />
    )
  }
  return teamLaunchWizardRedirect(
    input.holdWizardContinue,
    input.campaignView,
    input.campaignId,
  )
}

function TeamAssignmentSection({
  teamLoading,
  teamLoadError,
  teamLoadErrorMessage,
  onRetryTeam,
  assignableRows,
  selectedIds,
  campaignView,
  sessionUserId,
  onToggleOne,
}: Readonly<{
  teamLoading: boolean
  teamLoadError: boolean
  teamLoadErrorMessage: string
  onRetryTeam: () => void
  assignableRows: TeamMemberRow[]
  selectedIds: Set<string>
  campaignView: Campaign
  sessionUserId: string | undefined
  onToggleOne: (memberId: string, checked: boolean) => void
}>) {
  if (teamLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }
  if (teamLoadError) {
    return (
      <Box sx={{ mb: 2 }} data-testid="team-launch-load-error">
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {teamLoadErrorMessage}
        </Typography>
        <Button variant="outlined" size="small" onClick={onRetryTeam}>
          Retry
        </Button>
      </Box>
    )
  }
  if (assignableRows.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="team-launch-empty-members"
        sx={{
          textAlign: 'center',
          fontSize: 14,
          lineHeight: '20px',
          letterSpacing: '-0.15px',
          color: TEXT_SECONDARY,
          px: 2,
          py: { xs: 1.5, sm: 0 },
          maxWidth: 420,
          mx: 'auto',
        }}
      >
        No team members available. You can add team members from Team Management
      </Typography>
    )
  }
  return (
    <Stack spacing={1.5} sx={{ width: '100%' }} data-testid="team-launch-member-list">
      {assignableRows.map((m, index) => {
        const checked = selectedIds.has(m._id)
        const pill = memberAssignmentPill(m, campaignView, {
          sessionUserId,
          assignableMemberCount: assignableRows.length,
        })
        const avatarBg = AVATAR_BACKGROUNDS[index % AVATAR_BACKGROUNDS.length]
        const avatarFg = AVATAR_FG[index % AVATAR_FG.length]

        return (
          <Box
            key={m._id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: '13px',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: checked ? ROW_SELECTED_BORDER : BORDER_DEFAULT,
              bgcolor: checked ? ROW_SELECTED_BG : '#fff',
            }}
          >
            <Checkbox
              checked={checked}
              onChange={(_, c) => onToggleOne(m._id, c)}
              icon={<TeamCheckboxUncheckedIcon />}
              checkedIcon={<TeamCheckboxCheckedIcon />}
              inputProps={{
                'aria-label': `Assign ${m.firstName} ${m.lastName}`,
              }}
              sx={{
                p: 0.5,
                color: ACCENT,
                '&:hover': { bgcolor: 'transparent' },
              }}
            />
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: 12,
                fontWeight: 600,
                bgcolor: avatarBg,
                color: avatarFg,
              }}
              aria-hidden
            >
              {memberInitials(m.firstName, m.lastName)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: '20px',
                  color: TEXT_PRIMARY,
                }}
              >
                {m.firstName} {m.lastName}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: '16px',
                  color: TEXT_SECONDARY,
                }}
              >
                {m.email}
              </Typography>
            </Box>
            <Box
              sx={{
                borderRadius: 9999,
                px: 1.25,
                py: 0.25,
                bgcolor: pill.bgcolor,
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: '16px',
                  color: pill.color,
                  whiteSpace: 'nowrap',
                }}
              >
                {pill.label}
              </Typography>
            </Box>
          </Box>
        )
      })}
    </Stack>
  )
}

export function CampaignTeamLaunchPage() {
  const role = useSessionStore((s) => s.user?.role)
  const sessionUserId = useSessionStore((s) => s.user?.id)
  const { campaignId = '' } = useParams<{ campaignId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { showToast } = useToast()
  useDocumentTitle('Team & Launch')

  const continueState = readWizardContinueToTeamState(location.state)
  const fromWizardContinue = Boolean(
    continueState?.[WIZARD_CONTINUE_TO_TEAM_STATE],
  )
  const pinnedContinueCampaignRef = useRef<Campaign | undefined>(undefined)
  if (continueState?.campaign) {
    pinnedContinueCampaignRef.current = continueState.campaign
  }
  const pinnedContinueCampaign = pinnedContinueCampaignRef.current

  const {
    data: campaign,
    isLoading,
    isError,
    refetch,
  } = useCampaign(campaignId)
  const serverTeamReady = isServerTeamWizardReady(campaign)
  const campaignView =
    (serverTeamReady ? campaign : undefined) ??
    pinnedContinueCampaign ??
    continueState?.campaign ??
    campaign
  const holdWizardContinue = shouldHoldWizardContinueOnTeam(
    pinnedContinueCampaign,
    campaign,
    fromWizardContinue,
  )

  // US187: if history was replace-chained (Back would hit tenant home), send
  // the Admin to LOI Step 3 instead of leaving the wizard.
  const teamBackBlocker = useBlocker(({ historyAction, nextLocation }) =>
    shouldInterceptTeamWizardBrowserBack(
      historyAction,
      nextLocation.pathname,
      campaignId,
    ),
  )

  useEffect(() => {
    if (teamBackBlocker.state !== 'blocked') return
    teamBackBlocker.reset()
    navigate(`/campaigns/${campaignId}/lines-of-inquiry`, { replace: true })
  }, [teamBackBlocker, campaignId, navigate])

  const {
    data: teamData,
    isLoading: teamLoading,
    isError: teamLoadError,
    error: teamQueryError,
    refetch: refetchTeam,
  } = useTeamMembersForAssignment()
  const teamLoadErrorMessage = teamLoadError
    ? messageFromUnknownApiError(
        teamQueryError,
        'Could not load team members. Ensure platform-service and identity-service use the same S2S_SECRET, then restart both services.',
      )
    : ''
  const launchCampaign = useLaunchCampaign()

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const assignableRows = teamData ?? EMPTY_TEAM_ROWS

  useEffect(() => {
    if (!fromWizardContinue || !serverTeamReady) return
    pinnedContinueCampaignRef.current = undefined
    navigate(location.pathname, { replace: true, state: null })
  }, [fromWizardContinue, serverTeamReady, location.pathname, navigate])

  useEffect(() => {
    if (!campaignView) return
    const wanted = new Set(campaignView.assignedPlatformUserIds ?? [])
    if (wanted.size === 0) {
      setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()))
      return
    }
    const next = new Set<string>()
    for (const m of assignableRows) {
      if (memberIdentityKeys(m).some((k) => wanted.has(k))) {
        next.add(m._id)
      }
    }
    setSelectedIds((prev) => {
      if (prev.size !== next.size) return next
      for (const id of next) {
        if (!prev.has(id)) return next
      }
      return prev
    })
  }, [campaignView?.id, campaignView?.assignedPlatformUserIds, assignableRows])

  const toggleOne = useCallback((memberId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const n = new Set(prev)
      if (checked) n.add(memberId)
      else n.delete(memberId)
      return n
    })
  }, [])

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(assignableRows.map((m) => m._id)))
  }, [assignableRows])

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const handleLaunch = async () => {
    try {
      await launchCampaign.mutateAsync({
        campaignId,
        body: { teamMemberIds: [...selectedIds] },
      })
      showToast('Campaign active successfully', 'success')
      navigate(`/campaigns/${campaignId}`)
    } catch (e: unknown) {
      showToast(messageFromCampaignLaunchError(e), 'error')
    }
  }

  const shell = resolveTeamLaunchPageShell({
    role,
    isLoading,
    isError,
    pinnedContinueCampaign,
    campaignView,
    holdWizardContinue,
    campaignId,
    refetch: () => {
      Promise.resolve(refetch()).catch(() => undefined)
    },
  })
  if (shell) return shell

  const activeCampaign = campaignView as Campaign
  const loiCount = activeCampaign.linesOfInquiry?.length ?? 0
  const launchDisabled = launchCampaign.isPending || loiCount === 0

  const totalAssignableMembers = assignableRows.length
  const selectedMemberCount = selectedIds.size
  const hasAssignableMembers = totalAssignableMembers > 0
  const allMembersSelected =
    hasAssignableMembers &&
    selectedMemberCount === totalAssignableMembers

  const retryTeamLoad = () => {
    refetchTeam().catch(() => undefined)
  }

  return (
    <CampaignWizardChrome
      wizardStep={4}
      totalWizardSteps={4}
      activePhaseIndex={3}
      campaignId={campaignId}
    >
      <Box
        data-testid="campaign-team-launch-page"
        sx={{
          width: '100%',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          /** Short viewports: scroll instead of crushing empty copy against the launch panel. */
          overflowY: 'auto',
        }}
      >
        <Box sx={{ flexShrink: 0, mb: 3 }}>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 20,
                lineHeight: '28px',
                letterSpacing: '-0.45px',
                color: '#23262f',
                mb: 0.5,
              }}
            >
              Team & Launch
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: '20px',
                letterSpacing: '-0.15px',
                color: TEXT_SECONDARY,
              }}
            >
              Review and customize generated team for your campaign and launch it
            </Typography>
          </Box>
          {hasAssignableMembers && (
            <Stack
              direction="row"
              spacing={1.5}
              justifyContent="flex-end"
              flexWrap="wrap"
              sx={{ mt: 2 }}
            >
              <Button
                variant="contained"
                onClick={allMembersSelected ? deselectAll : selectAll}
                data-testid="team-launch-bulk-toggle"
                sx={{
                  borderRadius: '12px',
                  fontWeight: 500,
                  fontSize: 14,
                  lineHeight: '20px',
                  letterSpacing: '0.1px',
                  textTransform: 'none',
                  px: 2,
                  py: 1.25,
                  minHeight: 48,
                  boxSizing: 'border-box',
                  bgcolor: ACCENT,
                  color: '#fff',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: palettePrimaryDark, boxShadow: 'none' },
                }}
              >
                {allMembersSelected ? 'Deselect All' : 'Select All'}
              </Button>
            </Stack>
          )}
        </Box>

        <Box
          data-testid="team-launch-assignment-region"
          sx={{
            flex: '1 1 auto',
            minHeight: hasAssignableMembers ? 0 : { xs: 96, sm: 120 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: hasAssignableMembers ? 'flex-start' : 'center',
            overflowY: hasAssignableMembers ? 'auto' : 'visible',
            /** Keep empty-state copy clear of the navy launch panel on short screens. */
            mb: 3,
          }}
        >
          <TeamAssignmentSection
            teamLoading={teamLoading}
            teamLoadError={teamLoadError}
            teamLoadErrorMessage={teamLoadErrorMessage}
            onRetryTeam={retryTeamLoad}
            assignableRows={assignableRows}
            selectedIds={selectedIds}
            campaignView={activeCampaign}
            sessionUserId={sessionUserId}
            onToggleOne={toggleOne}
          />
        </Box>

        <Box
          data-testid="campaign-team-launch-ready-panel"
          sx={{
            flexShrink: 0,
            mt: 'auto',
            borderRadius: '10px',
            overflow: 'hidden',
            bgcolor: '#041E42',
            backgroundImage: LAUNCH_SUMMARY_PANEL_GRADIENT,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 100%',
            px: { xs: 2, sm: 2.5 },
            pt: 2.5,
            pb: 2.5,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 20,
              lineHeight: '28px',
              letterSpacing: '-0.45px',
              color: '#fff',
              textAlign: 'center',
              mb: 2.5,
            }}
          >
            Ready to Launch
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            useFlexGap
            sx={{
              mb: 2.5,
              justifyContent: 'center',
              alignItems: { xs: 'stretch', sm: 'center' },
              flexWrap: { sm: 'wrap' },
            }}
          >
            <ReadyLaunchStatCard
              icon={<AssignmentOutlinedIcon aria-hidden />}
              value={paddedLoiCount(loiCount)}
              label="Lines of Inquiries"
            />
            <ReadyLaunchStatCard
              icon={<AccountBalanceWalletOutlinedIcon aria-hidden />}
              value={formatBudgetDisplay(activeCampaign.budget)}
              label="Budget"
            />
            <ReadyLaunchStatCard
              icon={<CalendarMonthOutlinedIcon aria-hidden />}
              value={weekTimelineWeeks(activeCampaign)}
              label="Week Timeline"
            />
          </Stack>

          <Button
            fullWidth
            variant="contained"
            disabled={launchDisabled}
            onClick={() => {
              handleLaunch().catch(() => undefined)
            }}
            data-testid="campaign-team-launch-submit"
            sx={{
              maxWidth: 363,
              mx: 'auto',
              display: 'block',
              bgcolor: ACCENT,
              py: 2,
              px: 3,
              borderRadius: '8px',
              fontSize: 16,
              fontWeight: 500,
              letterSpacing: '0.15px',
              lineHeight: '24px',
              color: '#fff',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: palettePrimaryDark, boxShadow: 'none' },
            }}
          >
            {launchCampaign.isPending ? 'Launching…' : 'Launch Campaign'}
          </Button>
        </Box>
      </Box>
    </CampaignWizardChrome>
  )
}
